import { AxiosInstance } from 'axios'
import { useInfiniteQuery } from 'react-query'

import {
  TEAMS_BASE_QUERY_KEY,
  TeamDetails,
} from '@dc-shared-web/hooks/api/team'

import { useAuth } from '@dc-shared-web/providers/AuthProvider'
import { useAxiosContext } from '@dc-shared-web/providers/AxiosProvider'

type MultiTeamDetailsQueryParams = {
  page?: number
  limit?: number
  sortField?: string
  q?: string
  internal?: boolean
  active?: boolean
}

const fetchMultiTeamDetails = async (
  apiClient: AxiosInstance,
  params?: MultiTeamDetailsQueryParams
): Promise<TeamDetails> => {
  const defaultParams = {
    page: 0,
    limit: 25,
    sortField: 'name',
  }
  const { data } = await apiClient.get(`/api/teams`, {
    params: { ...defaultParams, ...params },
  })

  return data
}

/**
 * Custom hook to fetch multiple team details.
 *
 * @param params - Optional query parameters for fetching team details.
 * @returns The result of the query containing team details.
 */
export const useGetMultiTeamDetails = (
  params?: MultiTeamDetailsQueryParams
) => {
  const { user } = useAuth()
  const apiClient = useAxiosContext()

  return useInfiniteQuery<TeamDetails, Error>({
    queryKey: [`${TEAMS_BASE_QUERY_KEY}Details`, params],
    queryFn: ({ pageParam = 0 }) =>
      fetchMultiTeamDetails(apiClient, { ...params, page: pageParam + 1 }),
    getNextPageParam: (lastPage, pages) =>
      lastPage.meta.pages > pages.length ? pages.length : undefined,
    enabled: user.is_super_admin,
  })
}
